import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import { Button, Row, Table, Input, Label, ButtonGroup, Col } from "reactstrap";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import moment from "moment";

// (2026-08) Monitoreo de pedidos y producción: solicitado vs comprado (compras
// directas de las órdenes de corte externo), cortado, confección y entregado.
function ReporteEstatusPedidos() {
  const { user } = useContext(AuthContext);
  const URL_PEDIDOS = process.env.REACT_APP_URL_PEDIDOS;
  const URL_EMPRESAS = process.env.REACT_APP_URL_EMPRESAS;

  const hoy = new Date();
  const inicio = new Date();
  inicio.setDate(inicio.getDate() - 60);

  const [selectedFechaInicio, setSelectedFechaInicio] = useState(
    moment(inicio).format("YYYY-MM-DD")
  );
  const [selectedFechaFin, setSelectedFechaFin] = useState(
    moment(hoy).format("YYYY-MM-DD")
  );

  const [pedidos, setPedidos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  // (2026-09) filtro por cliente (sustituye al de marca)
  const [selectedCliente, setSelectedCliente] = useState("");
  const [selectedEmpresa, setSelectedEmpresa] = useState("");
  const [cargando, setCargando] = useState(false);

  // (2026-09) cliente con el subcliente entre paréntesis, como en ListadoPedidos
  const clienteConSub = (r) => {
    const sub = String(r?.subCliente || "").trim();
    const nombre = String(r?.cliente || "");
    return sub && sub.toUpperCase() !== "NA" ? `${nombre} (${sub})` : nombre;
  };

  const headers = {
    Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
  };

  const arr = (d) => (Array.isArray(d) ? d : []);
  const num = (n) => new Intl.NumberFormat("en-US").format(Number(n || 0));
  const money = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(Number(n || 0));
  const fechaCorta = (f) => (f ? moment(f).format("DD/MM/YYYY") : "-");

  // Días hábiles (sin sábados ni domingos) de hoy a la fecha de entrega
  const diasHabiles = (fechaEntrega) => {
    if (!fechaEntrega) return null;
    const fin = moment(fechaEntrega, "YYYY-MM-DD").startOf("day");
    const ini = moment().startOf("day");
    if (!fin.isValid()) return null;
    const signo = fin.isBefore(ini) ? -1 : 1;
    const a = signo > 0 ? ini.clone() : fin.clone();
    const b = signo > 0 ? fin.clone() : ini.clone();
    let dias = 0;
    while (a.isBefore(b)) {
      a.add(1, "day");
      const d = a.day();
      if (d !== 0 && d !== 6) dias++;
    }
    return dias * signo;
  };

  const textoDias = (d) => {
    if (d === null) return "-";
    if (d < 0) return `vencido ${Math.abs(d)} ${Math.abs(d) === 1 ? "día" : "días"}`;
    return `${d} ${d === 1 ? "día" : "días"}`;
  };

  useEffect(() => {
    axios
      .get(URL_EMPRESAS, { headers })
      .then((r) => setEmpresas(arr(r.data)))
      .catch((e) => console.log(e));
  }, []);

  useEffect(() => {
    setCargando(true);
    axios
      .get(
        `${URL_PEDIDOS}EstatusFechas/${selectedFechaInicio}/${selectedFechaFin}`,
        { headers }
      )
      .then((r) => {
        setPedidos(
          arr(r.data).sort((a, b) =>
            String(a.fechaEntrega || "").localeCompare(String(b.fechaEntrega || ""))
          )
        );
        setCargando(false);
      })
      .catch((e) => {
        console.log(e);
        setCargando(false);
        Swal.fire("Error", "No se pudo cargar el reporte de pedidos", "error");
      });
  }, [selectedFechaInicio, selectedFechaFin]);

  // (2026-09) Clientes del combo: sólo los que tienen pedidos en el periodo
  const clientesLista = useMemo(() => {
    const m = new Map();
    pedidos.forEach((p) => {
      const id = String(p.idCliente || "");
      if (id && !m.has(id)) m.set(id, String(p.cliente || "").trim() || id);
    });
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1], "es"));
  }, [pedidos]);

  useEffect(() => {
    if (selectedCliente && !clientesLista.some(([id]) => id === selectedCliente)) {
      setSelectedCliente("");
    }
  }, [clientesLista, selectedCliente]);

  const filtrados = useMemo(
    () =>
      pedidos.filter(
        (p) =>
          (!selectedCliente || p.idCliente === selectedCliente) &&
          (!selectedEmpresa || p.idEmpresa === selectedEmpresa)
      ),
    [pedidos, selectedCliente, selectedEmpresa]
  );

  const tot = useMemo(() => {
    const t = {
      monto: 0,
      solicitada: 0,
      comprada: 0,
      cortada: 0,
      confeccion: 0,
      entregada: 0,
      surtida: 0,
      enProduccion: 0,
    };
    filtrados.forEach((p) => {
      t.monto += Number(p.total_general || 0);
      t.solicitada += Number(p.solicitada || 0);
      t.comprada += Number(p.comprada || 0);
      t.cortada += Number(p.cortada || 0);
      t.confeccion += Number(p.confeccion || 0);
      t.entregada += Number(p.entregada || 0);
      t.surtida += Number(p.surtida || 0);
      t.enProduccion += Number(p.enProduccion || 0);
    });
    t.avance = t.solicitada > 0 ? (t.enProduccion / t.solicitada) * 100 : 0;
    return t;
  }, [filtrados]);

  const folios = useMemo(() => {
    const ids = filtrados.map((p) => Number(p.idPedido)).filter((n) => !isNaN(n));
    if (!ids.length) return "-";
    return `${Math.min(...ids)} al ${Math.max(...ids)}`;
  }, [filtrados]);

  // ---------------- PDF ----------------
  const PDFReporte = () => {
    if (!filtrados.length) {
      return Swal.fire("Sin datos", "No hay pedidos en el periodo/filtros", "warning");
    }

    const doc = new jsPDF("landscape", "mm", "a4");
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const ML = 10;
    const usableW = pageW - ML * 2;

    const negroAzul = [17, 24, 39];
    const azul = [59, 130, 246];
    const azulOscuro = [31, 56, 100];
    const gris = [110, 120, 135];
    const bordeGris = [210, 216, 224];
    const rojo = [190, 30, 45];
    const naranja = [214, 110, 40];
    const verde = [39, 124, 70];

    // ---- Banda de título
    doc.setFillColor(...negroAzul);
    doc.roundedRect(ML, 8, usableW, 18, 2, 2, "F");
    doc.setTextColor(255).setFont(undefined, "bold").setFontSize(14);
    doc.text("LANGIANI ERP — MONITOREO DE PEDIDOS Y PRODUCCIÓN", ML + 5, 16);
    doc.setFont(undefined, "normal").setFontSize(8);
    doc.text(
      "Módulo: Producción & Logística | Control de Avance, Compras, Taller y Ventas",
      ML + 5,
      21.5
    );

    const nombreCliente =
      clientesLista.find(([id]) => id === selectedCliente)?.[1] || "Todos";
    const nombreEmpresa =
      empresas.find((e) => e._id === selectedEmpresa)?.name || "Todas";

    doc.setFontSize(7.5);
    doc.text(
      `Fecha de Emisión: ${moment(hoy).format("DD/MM/YYYY")}`,
      pageW - ML - 5,
      13.5,
      { align: "right" }
    );
    doc.text(
      `Filtros: Cliente (${nombreCliente}) | Empresa (${nombreEmpresa})`,
      pageW - ML - 5,
      18,
      { align: "right" }
    );
    doc.setFont(undefined, "bold");
    doc.text(`Folios: ${folios}`, pageW - ML - 5, 22.5, { align: "right" });
    doc.setTextColor(0).setFont(undefined, "normal");

    // ---- Tarjetas
    let y = 30;
    const tarjetas = [
      { titulo: "TOTAL PEDIDOS", valor: String(filtrados.length), pie: `Folios ${folios}` },
      { titulo: "MONTO TOTAL", valor: money(tot.monto), pie: "MXN Solicitado" },
      { titulo: "CANT. SOLICITADA", valor: `${num(tot.solicitada)} Pzs`, pie: "Total Programado" },
      {
        titulo: "EN PRODUCCIÓN",
        valor: `${num(tot.enProduccion)} Pzs`,
        pie: `${filtrados.filter((p) => p.enProduccion > 0).length} Pedidos en Proceso`,
      },
      {
        titulo: "CANT. ENTREGADA",
        valor: `${num(tot.entregada)} Pzs`,
        pie: `${
          tot.solicitada > 0 ? ((tot.entregada / tot.solicitada) * 100).toFixed(0) : 0
        }% Despachado`,
      },
      {
        titulo: "% AVANCE GENERAL",
        valor: `${tot.avance.toFixed(1)}%`,
        pie: `${num(tot.enProduccion)} / ${num(tot.solicitada)} Pzs`,
      },
    ];

    const gap = 3;
    const tarjW = (usableW - gap * (tarjetas.length - 1)) / tarjetas.length;
    const tarjH = 18;
    tarjetas.forEach((t, i) => {
      const x = ML + i * (tarjW + gap);
      doc.setFillColor(255).setDrawColor(...bordeGris).setLineWidth(0.25);
      doc.roundedRect(x, y, tarjW, tarjH, 1.5, 1.5, "FD");
      doc.setFont(undefined, "bold").setFontSize(7).setTextColor(...gris);
      doc.text(t.titulo, x + tarjW / 2, y + 5, { align: "center" });
      doc.setFontSize(11).setTextColor(...azulOscuro);
      doc.text(t.valor, x + tarjW / 2, y + 11, { align: "center" });
      doc.setFont(undefined, "normal").setFontSize(6.5).setTextColor(...gris);
      doc.text(t.pie, x + tarjW / 2, y + 15.5, { align: "center" });
      doc.setTextColor(0);
    });

    y += tarjH + 5;

    // ---- Tabla (encabezado de dos renglones para agrupar PRODUCCION)
    doc.autoTable({
      startY: y,
      margin: { left: ML, right: ML, top: 12, bottom: 12 },
      tableWidth: usableW,
      theme: "grid",
      styles: {
        fontSize: 7.5,
        cellPadding: 1.4,
        valign: "middle",
        lineColor: bordeGris,
        lineWidth: 0.15,
        textColor: 40,
      },
      headStyles: {
        fillColor: negroAzul,
        textColor: [255, 255, 255],
        fontStyle: "bold",
        // chico, para que "SOLICITADA" / "CONFECCION" no se partan a media palabra
        fontSize: 6.5,
        halign: "center",
        cellPadding: 1,
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      head: [
        [
          { content: "PEDIDO", rowSpan: 2 },
          { content: "EMPRESA FABRICANTE", rowSpan: 2 },
          { content: "VENDEDOR", rowSpan: 2 },
          { content: "CLIENTE", rowSpan: 2 },
          { content: "FECHA\nPEDIDO", rowSpan: 2 },
          { content: "FECHA\nENTREGA", rowSpan: 2 },
          { content: "DÍAS\nHÁBILES", rowSpan: 2 },
          { content: "CANT.\nSOLICITADA", rowSpan: 2 },
          { content: "CANT.\nCOMPRADA", rowSpan: 2 },
          { content: "PRODUCCION", colSpan: 2, styles: { fillColor: azul } },
          { content: "CANT.\nENTREGADA", rowSpan: 2 },
          { content: "% AVANCE\nPRODUCCIÓN", rowSpan: 2 },
          { content: "CANT.\nSURTIDA", rowSpan: 2 },
        ],
        [
          { content: "CORTADA", styles: { fillColor: azul } },
          { content: "CONFECCION", styles: { fillColor: azul } },
        ],
      ],
      columnStyles: {
        0: { cellWidth: 13, halign: "center", fontStyle: "bold" },
        1: { cellWidth: 26 },
        2: { cellWidth: 22 },
        3: { cellWidth: 28, fontStyle: "bold" },
        4: { cellWidth: 18, halign: "center" },
        5: { cellWidth: 18, halign: "center" },
        6: { cellWidth: 18, halign: "center", fontStyle: "bold" },
        7: { cellWidth: 19, halign: "center", fontStyle: "bold" },
        8: { cellWidth: 19, halign: "center" },
        9: { cellWidth: 19, halign: "center" },
        10: { cellWidth: 19, halign: "center" },
        11: { cellWidth: 19, halign: "center" },
        12: { cellWidth: 19, halign: "center", fontStyle: "bold" },
        13: { halign: "center", fontStyle: "bold" },
      },
      body: filtrados.map((p) => {
        const d = diasHabiles(p.fechaEntrega);
        return [
          `#${p.idPedido}`,
          p.empresa || "-",
          p.vendedor || "-",
          clienteConSub(p) || "-",
          fechaCorta(p.fecha),
          fechaCorta(p.fechaEntrega),
          textoDias(d),
          num(p.solicitada),
          p.comprada > 0 ? num(p.comprada) : "-",
          p.cortada > 0 ? num(p.cortada) : "-",
          p.confeccion > 0 ? num(p.confeccion) : "-",
          num(p.entregada),
          `${Number(p.avance || 0).toFixed(0)}%`,
          num(p.surtida),
        ];
      }),
      foot: [[
        { content: "TOTALES GENERALES", colSpan: 7, styles: { halign: "left" } },
        num(tot.solicitada),
        tot.comprada > 0 ? num(tot.comprada) : "-",
        tot.cortada > 0 ? num(tot.cortada) : "-",
        tot.confeccion > 0 ? num(tot.confeccion) : "-",
        num(tot.entregada),
        `${tot.avance.toFixed(1)}%`,
        num(tot.surtida),
      ]],
      footStyles: {
        fillColor: [226, 233, 241],
        textColor: azulOscuro,
        fontStyle: "bold",
        halign: "center",
      },
      didParseCell: (h) => {
        if (h.section !== "body") return;
        const p = filtrados[h.row.index];
        if (!p) return;

        // Días hábiles: rojo si urge, naranja si está cerca
        if (h.column.index === 6) {
          const d = diasHabiles(p.fechaEntrega);
          if (d !== null && d <= 2) {
            h.cell.styles.textColor = rojo;
          } else if (d !== null && d <= 5) {
            h.cell.styles.textColor = naranja;
          }
        }

        // Avance con semáforo
        if (h.column.index === 12) {
          const a = Number(p.avance || 0);
          if (a >= 100) {
            h.cell.styles.fillColor = [216, 240, 224];
            h.cell.styles.textColor = verde;
          } else if (a > 0) {
            h.cell.styles.fillColor = [255, 244, 214];
            h.cell.styles.textColor = naranja;
          } else {
            h.cell.styles.textColor = gris;
          }
        }
      },
    });

    // ---- Pie
    const paginas = doc.internal.getNumberOfPages();
    for (let p = 1; p <= paginas; p++) {
      doc.setPage(p);
      doc.setDrawColor(...bordeGris).setLineWidth(0.2);
      doc.line(ML, pageH - 10, pageW - ML, pageH - 10);
      doc.setFont(undefined, "normal").setFontSize(7.5).setTextColor(...gris);
      doc.text(
        `LANGIANI ERP System © ${moment(hoy).format("YYYY")} | Documento Generado Automáticamente`,
        ML,
        pageH - 5.5
      );
      doc.text(`Página ${p} de ${paginas}`, pageW - ML, pageH - 5.5, { align: "right" });
      doc.setTextColor(0);
    }

    window.open(doc.output("bloburl"), "_blank");
  };

  const excel = () => {
    if (!filtrados.length) {
      return Swal.fire("Sin datos", "No hay pedidos en el periodo/filtros", "warning");
    }
    const option = {};
    option.fileName = "ReporteEstatusPedidos";
    option.datas = [
      {
        sheetData: filtrados.map((p) => ({
          Pedido: p.idPedido,
          Empresa: p.empresa,
          Vendedor: p.vendedor,
          Cliente: clienteConSub(p),
          FechaPedido: p.fecha,
          FechaEntrega: p.fechaEntrega,
          DiasHabiles: diasHabiles(p.fechaEntrega),
          Solicitada: p.solicitada,
          Comprada: p.comprada,
          Cortada: p.cortada,
          Confeccion: p.confeccion,
          Entregada: p.entregada,
          Avance: Number(p.avance || 0).toFixed(1),
          Surtida: p.surtida,
        })),
        sheetName: "Estatus Pedidos",
        sheetFilter: ["Pedido","Empresa","Vendedor","Cliente","FechaPedido","FechaEntrega","DiasHabiles","Solicitada","Comprada","Cortada","Confeccion","Entregada","Avance"],
        sheetHeader: ["Pedido","Empresa Fabricante","Vendedor","Cliente","Fecha Pedido","Fecha Entrega","Días Hábiles","Cant. Solicitada","Cant. Comprada","Cortada","Confección","Cant. Entregada","% Avance","Cant. Surtida"],
      },
    ];
    new ExportJsonExcel(option).saveExcel();
  };

  const colorDias = (d) => {
    if (d === null) return undefined;
    if (d <= 2) return { color: "#BE1E2D", fontWeight: "bold" };
    if (d <= 5) return { color: "#D66E28", fontWeight: "bold" };
    return undefined;
  };

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_ventas ? (
        <div className="card container col-12">
          <Row>
            <Col md={8}>
              <Button size="sm" href="/MenuVentas" className="btn btn-danger" id="botonListado">
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFReporte}>
                  PDF <i className="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i className="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <h3 align="center">Reporte Estatus Pedidos</h3>

          <Row>
            <Col md={2}>
              <Label>Fecha Inicio</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaInicio}
                onChange={(e) => setSelectedFechaInicio(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label>Fecha Fin</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaFin}
                onChange={(e) => setSelectedFechaFin(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Label>Cliente</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedCliente}
                onChange={(e) => setSelectedCliente(e.target.value)}
              >
                <option value="">Todos</option>
                {clientesLista.map(([id, nombre]) => (
                  <option key={id} value={id}>
                    {nombre}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={2}>
              <Label>Empresa</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedEmpresa}
                onChange={(e) => setSelectedEmpresa(e.target.value)}
              >
                <option value="">Todas</option>
                {empresas
                  .sort((a, b) => (a.name > b.name ? 1 : -1))
                  .map((e) => (
                    <option key={e._id} value={e._id}>
                      {e.name}
                    </option>
                  ))}
              </Input>
            </Col>
          </Row>

          <br />

          <Row>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">TOTAL PEDIDOS</small>
                <h4>{filtrados.length}</h4>
                <small className="text-muted">Folios {folios}</small>
              </div>
            </Col>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">MONTO TOTAL</small>
                <h4>{money(tot.monto)}</h4>
              </div>
            </Col>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">CANT. SOLICITADA</small>
                <h4>{num(tot.solicitada)} Pzs</h4>
              </div>
            </Col>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">EN PRODUCCIÓN</small>
                <h4>{num(tot.enProduccion)} Pzs</h4>
              </div>
            </Col>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">CANT. ENTREGADA</small>
                <h4>{num(tot.entregada)} Pzs</h4>
              </div>
            </Col>
            <Col md>
              <div className="card p-2 text-center">
                <small className="text-muted">% AVANCE GENERAL</small>
                <h4>{tot.avance.toFixed(1)}%</h4>
              </div>
            </Col>
          </Row>

          <br />
          {cargando ? <h5 align="center">Cargando...</h5> : null}

          <Table size="sm" striped borderless className="table-responsive-xl">
            <thead>
              <tr>
                <th className="text-center">Pedido</th>
                <th>Empresa Fabricante</th>
                <th>Vendedor</th>
                <th>Cliente</th>
                <th className="text-center">Fecha Pedido</th>
                <th className="text-center">Fecha Entrega</th>
                <th className="text-center">Días Hábiles</th>
                <th className="text-center">Cant. Solicitada</th>
                <th className="text-center">Cant. Comprada</th>
                <th className="text-center">Cortada</th>
                <th className="text-center">Confección</th>
                <th className="text-center">Cant. Entregada</th>
                <th className="text-center">% Avance</th>
                <th className="text-center">Cant. Surtida</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.length ? (
                filtrados.map((p) => {
                  const d = diasHabiles(p.fechaEntrega);
                  return (
                    <tr key={p._id}>
                      <td className="text-center negrita">#{p.idPedido}</td>
                      <td>{p.empresa}</td>
                      <td>{p.vendedor}</td>
                      <td className="negrita">{clienteConSub(p)}</td>
                      <td className="text-center">{fechaCorta(p.fecha)}</td>
                      <td className="text-center">{fechaCorta(p.fechaEntrega)}</td>
                      <td className="text-center" style={colorDias(d)}>
                        {textoDias(d)}
                      </td>
                      <td className="text-center negrita">{num(p.solicitada)}</td>
                      <td className="text-center">
                        {p.comprada > 0 ? num(p.comprada) : "-"}
                      </td>
                      <td className="text-center">
                        {p.cortada > 0 ? num(p.cortada) : "-"}
                      </td>
                      <td className="text-center">
                        {p.confeccion > 0 ? num(p.confeccion) : "-"}
                      </td>
                      <td className="text-center">{num(p.entregada)}</td>
                      <td className="text-center negrita">
                        {Number(p.avance || 0).toFixed(0)}%
                      </td>
                      <td className="text-center negrita">{num(p.surtida)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="14" className="text-center text-muted">
                    No hay pedidos en el periodo seleccionado
                  </td>
                </tr>
              )}
              {filtrados.length ? (
                <tr>
                  <td colSpan="7" className="negrita">
                    TOTALES GENERALES
                  </td>
                  <td className="text-center negrita">{num(tot.solicitada)}</td>
                  <td className="text-center negrita">{num(tot.comprada)}</td>
                  <td className="text-center negrita">{num(tot.cortada)}</td>
                  <td className="text-center negrita">{num(tot.confeccion)}</td>
                  <td className="text-center negrita">{num(tot.entregada)}</td>
                  <td className="text-center negrita">{tot.avance.toFixed(1)}%</td>
                  <td className="text-center negrita">{num(tot.surtida)}</td>
                </tr>
              ) : null}
            </tbody>
          </Table>
          <br />
        </div>
      ) : undefined}
      <br />
    </>
  );
}

export default ReporteEstatusPedidos;
